import { Users, Briefcase, Award, ArrowUpRight, GraduationCap, ChevronRight, CheckCircle2, Phone, BookOpen, LayoutGrid, Download, Star, StarHalf } from "lucide-react";
import { motion } from "motion/react";
import { InternshipDomain } from "../types";
import heroGroupImage from "../assets/images/IMG_20260611_160613.jpg";

interface GeneralHomeHeroProps {
  allDomains: InternshipDomain[];
  onSelectDomain: (domain: InternshipDomain) => void;
  openModal: (mode: "apply" | "brochure") => void;
}

export default function GeneralHomeHero({ allDomains, onSelectDomain, openModal }: GeneralHomeHeroProps) {
  // Let's filter some key stats/aspects to highlight
  return (
    <section className="relative text-slate-800 pt-8 pb-16 overflow-hidden" id="general-home-hero">
      {/* Cinematic grid layer behind the content */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#E7E4DF_1px,transparent_1px),linear-gradient(to_bottom,#E7E4DF_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 -z-10"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Core Hero Announcement Card */}
        <div className="relative rounded-[32px] overflow-hidden p-8 md:p-14 lg:p-18 bg-gradient-to-br from-[#FAF5EE] via-[#F2EAE0] to-[#EAE0D3] shadow-xl border border-[#DECDB8]/45">
          {/* Ambient Warm Glowing Backdrops */}
          <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-orange-500/10 rounded-full blur-[100px] pointer-events-none z-0"></div>
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[100px] pointer-events-none z-0"></div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center relative z-10">
            
            {/* Left Side: Editorial display headline, text, benefits, & admissions openers */}
            <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
              
              <div className="space-y-4">
                {/* Micro announcement badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/95 border border-slate-250/65 rounded-full text-[10px] font-mono font-bold tracking-widest text-orange-650 uppercase shadow-xs">
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                  HITASOFT TALENT LAB • MADURAI
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-black tracking-tight leading-[1.08] text-slate-950">
                  Career-Focused <br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-600 via-red-600 to-amber-600 font-extrabold block mt-2">
                    IT Internship Program in Madurai
                  </span>
                </h1>

                <h2 className="text-xl sm:text-2xl font-display font-bold text-slate-800 tracking-tight mt-3 leading-tight">
                  Build Actual Products and Emerge as a Skilled Professional
                </h2>

                {/* Descriptive subhead paragraph */}
                <p className="font-sans text-slate-700 text-xs sm:text-sm leading-relaxed max-w-xl font-medium mt-1">
                  Gain industry-ready skills through live projects and expert mentorship. Work under experienced team leads, build production-ready applications, and unlock placement opportunities with global technology partners.
                </p>
              </div>

              {/* Unique selling bullets */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2 text-xs font-semibold text-slate-800 font-sans">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20 text-orange-650 font-bold">✓</span>
                  <span>1-on-1 Real Code Review Sprints</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20 text-orange-650 font-bold">✓</span>
                  <span>Verifiable Internship Certificate</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20 text-orange-650 font-bold">✓</span>
                  <span>Live Production-Grade Portfolios</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20 text-orange-650 font-bold">✓</span>
                  <span>Admissions open for All Streams</span>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-5 pt-3 border-t border-slate-900/5 font-mono">
                <div className="flex items-center gap-2 font-black text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 shadow-sm text-sm sm:text-base transition-transform hover:scale-105">
                  <div className="flex items-center gap-0.5">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500" />
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500" />
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500" />
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500" />
                    <StarHalf className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-500 text-amber-500" />
                  </div>
                  <span>4.6 / 5 Google Verified Rating</span>
                </div>

              </div>

              {/* Call-to-Actions */}
              <div className="space-y-4 pt-2">
                {/* Main Interactive CTA Row */}
                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
                  <button
                    onClick={() => openModal("apply")}
                    className="px-6 py-3.5 bg-orange-650 hover:bg-orange-700 text-white font-bold text-xs tracking-wider uppercase rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer text-center flex items-center justify-center gap-2 shadow-lg shadow-orange-500/10"
                  >
                    <Users className="w-4 h-4 shrink-0" />
                    Enroll Now
                  </button>

                  <button
                    onClick={() => openModal("brochure")}
                    className="px-6 py-3.5 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 hover:text-slate-900 font-bold text-xs tracking-wider uppercase rounded-xl transition-all duration-200 hover:-translate-y-0.5 text-center flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                  >
                    <Download className="w-4 h-4 shrink-0 text-orange-600" />
                    Download brochure
                  </button>
                </div>

                {/* Sub links to Curriculum and Projects */}
                <div className="flex flex-wrap items-center gap-6 pt-1 text-xs font-mono font-extrabold text-slate-500">
                  <button
                    onClick={() => {
                      const el = document.getElementById("internships");
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                    className="hover:text-orange-655 flex items-center gap-1.5 transition-colors cursor-pointer uppercase"
                  >
                    <BookOpen className="w-4 h-4 text-orange-500" />
                    View Curriculum →
                  </button>
                  
                  <button
                    onClick={() => {
                      const el = document.getElementById("internship-gallery-section");
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                    className="hover:text-orange-655 flex items-center gap-1.5 transition-colors cursor-pointer uppercase"
                  >
                    <LayoutGrid className="w-4 h-4 text-orange-500" />
                    View Student Projects →
                  </button>
                </div>
              </div>

            </div>

            {/* Right Side: Showcase Image Frame and active Admissions Tag */}
            <div className="lg:col-span-5 relative flex items-center justify-center">
              <div className="relative w-full max-w-sm h-[320px] md:h-[390px] rounded-[30px] border-4 border-white shadow-2xl overflow-hidden bg-slate-900 flex items-center justify-center group">
                <img
                  src={heroGroupImage}
                  alt="Hitasoft developers working on real SaaS modules"
                  className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent"></div>

                {/* Floating indicator */}
                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md p-4 rounded-2xl shadow-xl border border-slate-200/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/20 shrink-0 text-orange-650 font-extrabold">H</div>
                    <div>
                      <span className="text-[9px] font-mono font-bold text-orange-650 uppercase tracking-wider block">Corporate Lab</span>
                      <span className="text-xs font-bold text-slate-850 leading-none">Global Tech Agency</span>
                    </div>
                  </div>
                  <span className="text-[10px] bg-slate-950 text-white font-bold px-2.5 py-1 rounded-full uppercase tracking-widest font-mono">
                    Hitasoft
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Global Statistics Ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 max-sm:grid-cols-1">
          <div className="bg-white border border-[#EAE2D5] shadow-xs px-5 py-4 rounded-2xl flex items-center gap-4.5 transition-all duration-300 hover:scale-[1.025] hover:shadow-md">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20 shrink-0">
              <Users className="w-5 h-5 text-orange-650" />
            </div>
            <div>
              <div className="text-lg md:text-xl font-display font-black text-slate-950 leading-tight">4,450+</div>
              <div className="text-[10px] uppercase font-bold tracking-tight text-slate-500 font-mono">Alumni Trained</div>
            </div>
          </div>

          <div className="bg-white border border-[#EAE2D5] shadow-xs px-5 py-4 rounded-2xl flex items-center gap-4.5 transition-all duration-300 hover:scale-[1.025] hover:shadow-md">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20 shrink-0">
              <Briefcase className="w-5 h-5 text-orange-650" />
            </div>
            <div>
              <div className="text-lg md:text-xl font-display font-black text-slate-950 leading-tight">15+</div>
              <div className="text-[10px] uppercase font-bold tracking-tight text-slate-500 font-mono">Company Partners</div>
            </div>
          </div>

          <div className="bg-white border border-[#EAE2D5] shadow-xs px-5 py-4 rounded-2xl flex items-center gap-4.5 transition-all duration-300 hover:scale-[1.025] hover:shadow-md">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20 shrink-0">
              <Award className="w-5 h-5 text-orange-650" />
            </div>
            <div>
              <div className="text-lg md:text-xl font-display font-black text-slate-950 leading-tight">98.5%</div>
              <div className="text-[10px] uppercase font-bold tracking-tight text-slate-500 font-mono">Placements Sync</div>
            </div>
          </div>

          <div className="bg-white border border-[#EAE2D5] shadow-xs px-5 py-4 rounded-2xl flex items-center gap-4.5 transition-all duration-300 hover:scale-[1.025] hover:shadow-md">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20 shrink-0">
              <GraduationCap className="w-5 h-5 text-orange-650" />
            </div>
            <div>
              <div className="text-lg md:text-xl font-display font-black text-slate-950 leading-tight">100%</div>
              <div className="text-[10px] uppercase font-bold tracking-tight text-slate-500 font-mono">Practical Code</div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
